import { FlightParams } from '../flights.types';
import { createAsyncThunk } from '@reduxjs/toolkit';
import adserver from 'services/adserver';
import { withErrorHandling } from 'store/wrappers';
import { REDUCER_KEY } from './counterOfferFlights.consts';
import { selectCurrentProposalId } from 'store/proposals';
import {
  normalizeDateFromApi,
  normalizeDateForApi,
} from 'helpers/datetimeHelpers';

function normalizeApiReponse(data: FlightParams) {
  const startDate = normalizeDateFromApi(data.timedProductValue.startDate);
  const endDate = normalizeDateFromApi(data.timedProductValue.endDate);
  return {
    ...data,
    timedProductValue: { ...data.timedProductValue, startDate, endDate },
  };
}

function normalizeApiRequest(data: FlightParams) {
  const startDate = normalizeDateForApi(data.timedProductValue.startDate);
  const endDate = normalizeDateForApi(data.timedProductValue.endDate);
  return {
    ...data,
    timedProductValue: { ...data.timedProductValue, startDate, endDate },
  };
}

export const getCounterOfferFlights = createAsyncThunk(
  REDUCER_KEY + '/getCounterOfferFlights',
  withErrorHandling(async ({ proposalId }: { proposalId: number }) => {
    const response = await adserver({
      url: `/proposal/${proposalId}/counteroffer/flights`,
      method: 'GET',
      headers: {},
    });
    const surcharges = await adserver({
      url: `/proposal/${proposalId}/surcharges`,
      headers: {},
    });
    return response.data.map((flight: FlightParams) => {
      return {
        ...normalizeApiReponse({
          ...flight,
          proposalId,
        }),
        surcharges: surcharges?.data,
      };
    });
  }),
);

export const createCounterOfferFlight = createAsyncThunk(
  REDUCER_KEY + '/createCounterOfferFlight',
  withErrorHandling(async (values: FlightParams, thunkAPI: any) => {
    const proposalId = selectCurrentProposalId(thunkAPI.getState());

    if (!proposalId) {
      throw new Error('proposalId not found');
    }
    const response = await adserver({
      url: `/proposal/${proposalId}/counteroffer/flight`,
      method: 'POST',
      data: normalizeApiRequest(values),
      headers: {},
    });
    return response.data.map((flight: FlightParams) => {
      return {
        ...normalizeApiReponse({
          ...flight,
          proposalId,
        }),
      };
    });
  }),
);

export const updateCounterOfferFlight = createAsyncThunk(
  REDUCER_KEY + '/updateCounterOfferFlight',
  withErrorHandling(async (data: FlightParams) => {
    const { id: flightId, proposalId } = data;

    if (!proposalId || !flightId) {
      throw new Error('Missing proposal or flight identifier');
    }

    const requestData = normalizeApiRequest(data);
    const response = await adserver({
      url: `/proposal/${proposalId}/counteroffer/flight/${flightId}`,
      method: 'PATCH',
      data: requestData,
      headers: {},
    });

    return normalizeApiReponse({ ...response.data, proposalId });
  }),
);

export const deleteCounterOfferFlight = createAsyncThunk(
  REDUCER_KEY + '/deleteCounterOfferFlight',
  withErrorHandling(
    async ({
      proposalId,
      flightId,
    }: {
      proposalId: number;
      flightId: number;
    }) => {
      await adserver({
        url: `/proposal/${proposalId}/counteroffer/flight/${flightId}`,
        method: 'DELETE',
        headers: {},
      });
      return flightId;
    },
  ),
);
